import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  ErrorHandler,
  Injector,
  NgModule,
  NgZone
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { NoopAnimationsModule } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { AuthModule, AuthService } from '@auth0/auth0-angular';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { NgxPageScrollCoreModule } from 'ngx-page-scroll-core';
import {
  provideNgxWebstorage,
  withLocalStorage,
  withNgxWebstorageConfig
} from 'ngx-webstorage';
import { AppComponent } from './app.component';
import { AppRouter } from './app.route';
import { HomeComponent } from './home/home.component';
import { ModalsModule } from './modals/modals.module';
import { ActivitiesResolveGuard } from './resolvers/activities.resolver';
import { AppResolveGuard } from './resolvers/app.resolver';
import { BusinessesResolveGuard } from './resolvers/businesses.resolver';
import { CompaniesResolveGuard } from './resolvers/companies.resolver';
import { DashboardResolveGuard } from './resolvers/dashboard.resolver';
import { EquipmentResolveGuard } from './resolvers/equipment.resolver';
import { ErrorsResolveGuard } from './resolvers/errors.resolver';
import { EulaResolveGuard } from './resolvers/eula.resolver';
import { FarmsResolveGuard } from './resolvers/farms.resolver';
import { FieldsResolveGuard } from './resolvers/fields.resolver';
import { InspectionsResolveGuard } from './resolvers/inspections.resolver';
import { JobsResolveGuard } from './resolvers/jobs.resolver';
import { OnboardingResolveGuard } from './resolvers/onboarding.resolver';
import { PersonnelResolveGuard } from './resolvers/personnel.resolver';
import { PlansResolveGuard } from './resolvers/plans.resolver';
import { ProductsResolveGuard } from './resolvers/products.resolver';
import { SetupResolveGuard } from './resolvers/setup.resolver';
import { StockResolveGuard } from './resolvers/stock.resolver';
import { TradeTypesResolveGuard } from './resolvers/trade-types.resolver';
import { TradesResolveGuard } from './resolvers/trades.resolver';
import { BackSaveDiscardGuard } from './shared/guards/back-save-discard/back-save-discard.guard';
import { JobSearchOptionsGuard } from './shared/guards/job-search-options.guard';
import { DeferredErrorHandler } from './shared/handlers/deferred-error.handler';
import { IconModule } from './shared/icon.module';
import { CacheInterceptor } from './shared/interceptors/cache.interceptor';
import { ErrorInterceptor } from './shared/interceptors/error.interceptor';
import { VersionInterceptor } from './shared/interceptors/version.interceptor';
import { AdminUserPermissionGuard } from './shared/services/authorisation/admin-user-permission.guard';
import { AuthGuard } from './shared/services/authorisation/authentication.guard';
import { BusinessCountGuard } from './shared/services/authorisation/business-count.guard';
import { ProductSegmentGuard } from './shared/services/authorisation/product-segment.guard';
import { UserPermissionGuard } from './shared/services/authorisation/user-permission.guard';
import { EnvService } from './shared/services/env/env.service';
import { SharedServicesModule } from './shared/services/services.module';
import { SharedModule } from './shared/shared.module';

// AoT requires an exported function for factories
export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient, 'assets/i18n/', '.json');
}

@NgModule({
  declarations: [AppComponent, HomeComponent],
  exports: [TranslateModule, SharedServicesModule, SharedModule, ModalsModule],
  bootstrap: [AppComponent],
  imports: [
    // Modules
    BrowserModule,
    AuthModule.forRoot(),
    NoopAnimationsModule,
    NgxPageScrollCoreModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    SharedServicesModule,
    SharedModule,
    NgxMaskDirective,
    NgxMaskPipe,
    AppRouter,
    IconModule
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: (envService: EnvService) => () => envService.init(),
      deps: [EnvService, HttpClient],
      multi: true
    },
    // route guards
    AuthGuard,
    BackSaveDiscardGuard,
    ProductSegmentGuard,
    JobSearchOptionsGuard,
    BusinessCountGuard,
    // resolve guards
    AppResolveGuard,
    ActivitiesResolveGuard,
    BusinessesResolveGuard,
    CompaniesResolveGuard,
    DashboardResolveGuard,
    EquipmentResolveGuard,
    EulaResolveGuard,
    ErrorsResolveGuard,
    FarmsResolveGuard,
    FieldsResolveGuard,
    InspectionsResolveGuard,
    JobsResolveGuard,
    OnboardingResolveGuard,
    PersonnelResolveGuard,
    PlansResolveGuard,
    ProductsResolveGuard,
    SetupResolveGuard,
    StockResolveGuard,
    TradeTypesResolveGuard,
    TradesResolveGuard,
    UserPermissionGuard,
    AdminUserPermissionGuard,
    { provide: HTTP_INTERCEPTORS, useClass: VersionInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
      deps: [AuthService]
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CacheInterceptor,
      multi: true
    },
    {
      provide: ErrorHandler,
      useClass: DeferredErrorHandler,
      deps: [Injector, NgZone]
    },
    provideNgxMask(),
    provideAnimationsAsync('noop'),
    provideHttpClient(withInterceptorsFromDi()),
    provideNgxWebstorage(
      withNgxWebstorageConfig({
        prefix: '',
        separator: '',
        caseSensitive: true
      }),
      withLocalStorage()
    ),
    provideCharts(withDefaultRegisterables())
  ]
})
export class AppModule {}
